/**
 * Backend-pending registry — the single source of truth for which CMS "platform
 * operations" pages are gated behind an honest "not connected yet" panel.
 *
 * Each id below is a page whose supporting CMS API endpoint does NOT exist yet.
 * This is PLANNED work, not abandoned work — the backend for these screens is
 * still coming. Until it ships, the route renders `BackendPendingPage` instead
 * of firing requests at URLs that 404 (or, worse, showing fabricated numbers).
 *
 * Re-enabling a page is the entire procedure: DELETE its id from this array.
 * `BackendPendingGate` reads this list at runtime, so removing one line restores
 * the real page — no other change is required.
 *
 * The source of truth for "does the endpoint actually exist" is
 * `api.cms.swagger.json`; add an id here only when its backend is confirmed
 * missing, and remove it the moment the contract lands.
 *
 * Keyed on sidebar MENU id (not path) so one key gates both the nav entry
 * (`sidebar.tsx` / the command palette) and the route (`App.tsx` → BackendPendingGate).
 * Ids must match `NAV_GROUPS` in `src/components/layout/navConfig.ts`.
 */
export const BACKEND_PENDING_MENU_IDS = [
  "dashboard",
  "financial", // Financial Operations (sidebar id is "financial"; path is /financial-operations)
  "billing",
  "analytics",
  "localization",
  "admin",
  "customizations",
  "help", // page arrives with ps-cms-help / ps-cms-engagement — that child deletes its line
] as const;

export type BackendPendingMenuId = (typeof BACKEND_PENDING_MENU_IDS)[number];

/** Whether the given sidebar menu id is gated as backend-pending. */
export function isBackendPending(menuId: string): boolean {
  return (BACKEND_PENDING_MENU_IDS as readonly string[]).includes(menuId);
}
